import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api, money, formatDate, downloadPdf } from "@/lib/api";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";
import { useLeviWorkspaceHint } from "@/lib/leviWorkspace";
import { makeTranslator } from "@/lib/onboarding_i18n";
import { Mark } from "@/components/Logo";
import CustomerPreview from "@/components/CustomerPreview";
import SharingCard from "@/components/SharingCard";
import { ArrowLeft, Receipt, Printer, PenLine, Link2, Eye, Mail, Download } from "lucide-react";
import { isTm, deriveDisclosureTotals } from "@/lib/disclosure";
import { newRequestKey, localizeSendError } from "@/lib/emailSend";

// Slice 2D4 Batch 3 · Localize the raw stored status enum for contractor list
// display. DB value is preserved; only the label is translated.
const localizeStatus = (t, status) => {
  if (!status) return status;
  const key = `contracts_list_page.status_labels.${status}`;
  const val = t(key);
  return val === key ? status : val;
};

export const ContractsList = () => {
  const { lang, t } = useLang();
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/contracts").then((r) => setItems(r.data)); }, []);
  return (
    <DashboardLayout title={t("pages.contracts.title")} subtitle={t("pages.contracts.subtitle")}>
      {items.length === 0 ? (
        <div className="jc-card p-12 text-center">
          <div className="font-display text-xl">{t("pages.contracts.empty_title")}</div>
          <p className="text-sm text-[var(--jc-text-soft)] mt-2">{t("pages.contracts.empty_body")}</p>
          <Link to="/app/proposals" className="inline-flex items-center mt-5 px-4 py-2 rounded-md jc-btn-blue text-sm font-semibold">{t("contracts_list_page.view_proposals_btn")}</Link>
        </div>
      ) : (
        <div className="jc-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[var(--jc-bg)] text-[var(--jc-text-soft)]">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">{t("contracts_list_page.columns.customer")}</th>
                <th className="text-left px-5 py-3 font-semibold">{t("contracts_list_page.columns.total")}</th>
                <th className="text-left px-5 py-3 font-semibold">{t("contracts_list_page.columns.status")}</th>
                <th className="text-left px-5 py-3 font-semibold hidden md:table-cell">{t("contracts_list_page.columns.created")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--jc-border)]">
              {items.map((c) => (
                <tr key={c.id} className="hover:bg-[var(--jc-bg)]">
                  <td className="px-5 py-3 font-semibold"><Link to={`/app/contracts/${c.id}`} data-testid={`contract-row-${c.id}`}>{c.snapshot?.customer?.name || "—"}</Link></td>
                  <td className="px-5 py-3 font-mono-jc">{money(c.snapshot?.totals?.total)}</td>
                  <td className="px-5 py-3"><span className="text-xs px-2 py-1 rounded-full bg-[var(--jc-border)]">{localizeStatus(t, c.status)}</span></td>
                  <td className="px-5 py-3 hidden md:table-cell text-[var(--jc-text-soft)]">{formatDate(c.created_at, lang)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </DashboardLayout>
  );
};

export const ContractDetail = () => {
  const { t } = useLang();
  const { cid } = useParams();
  const navigate = useNavigate();
  // Phase 5A · Publish current-page workspace hint.
  useLeviWorkspaceHint("contract_detail", {
    open_document_type: "contract",
    open_document_id: cid,
    current_section: "contract_detail",
  });
  const [c, setC] = useState(null);
  const [signing, setSigning] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);

  useEffect(() => { api.get(`/contracts/${cid}`).then((r) => setC(r.data)); }, [cid]);
  if (!c) return <DashboardLayout title={t("pages.contracts.detail_loading_title")} subtitle={t("pages.contracts.detail_loading_subtitle")}><div /></DashboardLayout>;

  const sign = async () => {
    if (!signing.trim()) { toast.error(t("contract_detail.toast_sign_missing_name")); return; }
    try {
      const { data } = await api.post(`/contracts/${cid}/sign`, { signature_name: signing });
      setC(data); toast.success(t("contract_detail.toast_signed"));
    } catch { toast.error(t("contract_detail.toast_sign_failed")); }
  };

  const makeDeposit = async () => {
    try {
      const deposit = c.snapshot?.totals?.deposit || 0;
      const { data } = await api.post("/invoices", {
        project_id: c.project_id, invoice_type: "deposit", amount: deposit,
      });
      toast.success(t("contract_detail.toast_deposit_created"));
      navigate(`/app/invoices/${data.id}`);
    } catch { toast.error(t("contract_detail.toast_deposit_failed")); }
  };

  const copyPublicLink = async () => {
    const url = `${window.location.origin}/c/${c.public_token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("contract_detail.toast_link_copied"));
    } catch {
      window.prompt(t("contract_detail.prompt_copy_link"), url);
    }
  };

  const emailToCustomer = async () => {
    if (sendingRef.current) return;
    const defaultEmail = c.snapshot?.customer?.email || "";
    const to = window.prompt(t("contract_detail.prompt_email_to"), defaultEmail);
    if (!to) return;
    const request_key = newRequestKey();
    sendingRef.current = true;
    setSending(true);
    try {
      const { data } = await api.post(`/email/contract/${cid}`, { to_email: to, request_key });
      const label = data.mock ? t("contract_detail.toast_email_mock") : t("contract_detail.toast_email_sent");
      toast.success(`${label} — ${to}`);
    } catch (e) {
      toast.error(localizeSendError(e, t, "contract_detail.toast_email_failed"));
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  const downloadContractPdf = async () => {
    try {
      const filename = await downloadPdf(`/contracts/${cid}/pdf`, "contract.pdf");
      toast.success(t("contract_detail.toast_downloaded", filename));
    } catch {
      toast.error(t("contract_detail.toast_pdf_failed"));
    }
  };

  const s = c.snapshot || {};
  const totals = s.totals || {};
  // Phase 2 Step 4: shared helper handles legacy snapshots and keeps the
  // contractor's contract view in lockstep with the public contract.
  const { tmEstimate, allowanceTotal } = deriveDisclosureTotals(s);

  // Slice 2D4 Batch 3 · The contract body (title, section labels, disclaimer
  // heading, totals labels, T&M block) is a document surface — it must render
  // in the saved doc language so print + PDF stay in lockstep with the
  // customer-visible link. Contractor-entered fields (contract_terms, scope,
  // exclusions, customer_responsibilities, change_order_language,
  // legal_disclaimer, customer name, address) are rendered as-stored.
  const docLang = c.language || s.language || "en";
  const dt = makeTranslator(docLang);
  const docStatusKey = `contract_doc.status_labels.${c.status}`;
  const statusDocLabel = dt(docStatusKey);
  const statusDocDisplay = statusDocLabel === docStatusKey ? c.status : statusDocLabel;

  return (
    <DashboardLayout
      title={t("contract_detail.subtitle_prefix", s.customer?.name || "")}
      subtitle={localizeStatus(t, c.status)}
      actions={
        <div className="flex items-center gap-2">
          <button onClick={() => navigate(-1)} className="text-sm text-[var(--jc-text-soft)] inline-flex items-center gap-1"><ArrowLeft size={14}/> {t("contract_detail.back")}</button>
          <button
            onClick={() => setPreviewOpen(true)}
            data-testid="contract-preview-customer-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)]"
          >
            <Eye size={14}/> {t("contract_detail.preview_customer_view")}
          </button>
          <button onClick={() => window.print()} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md border border-[var(--jc-border-strong)]"><Printer size={14}/> {t("contract_detail.print")}</button>
          <button onClick={downloadContractPdf} data-testid="contract-download-pdf-btn" className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md border border-[var(--jc-border-strong)]"><Download size={14}/> {t("contract_detail.download_pdf")}</button>
          {c.status === "signed" && (
            <button onClick={makeDeposit} data-testid="contract-deposit-invoice-btn" className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md jc-btn-blue"><Receipt size={14}/> {t("contract_detail.create_deposit_invoice")}</button>
          )}
        </div>
      }
    >
      <div className="max-w-4xl mx-auto mb-4">
        <SharingCard
          kind="contract"
          docId={cid}
          doc={c}
          onDocRefresh={() => api.get(`/contracts/${cid}`).then((r) => setC(r.data))}
        />
      </div>
      <div className="max-w-4xl mx-auto bg-white border border-[var(--jc-border)] rounded-sm shadow-[0_8px_30px_rgba(0,0,0,0.04)] p-10 print:shadow-none print:border-0">
        <header className="flex items-start justify-between border-b border-[var(--jc-border)] pb-6">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Mark size={32} />
              <div className="font-display text-lg">{s.company?.name || dt("contract_doc.your_company_fallback")}</div>
            </div>
            <div className="overline">{dt("contract_doc.kind_label")}</div>
            <h1 className="font-display text-3xl mt-1">{dt("contract_doc.agreement_with", s.customer?.name || "")}</h1>
            <div className="text-sm text-[var(--jc-text-soft)] mt-1">{s.customer?.address}</div>
          </div>
          <div className="text-right text-sm text-[var(--jc-text-soft)]">
            <div>{dt("contract_doc.date_label")}: {formatDate(c.created_at, docLang)}</div>
            <div>{dt("contract_doc.status_label")}: <span className="font-semibold text-[var(--jc-text)]">{statusDocDisplay}</span></div>
            {c.language && (
              <div className="mt-1">
                <span data-testid="contract-lang-badge" className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider bg-[var(--jc-border)] text-[var(--jc-text)]">
                  {c.language.toUpperCase()}
                </span>
              </div>
            )}
          </div>
        </header>

        <section className="mt-6 text-sm space-y-5">
          <Block title={dt("contract_doc.section_scope")} body={s.scope} />
          <Block title={dt("contract_doc.section_terms")} body={c.contract_terms} />
          <Block title={dt("contract_doc.section_change_orders")} body={c.change_order_language} />
          <Block title={dt("contract_doc.section_customer_responsibilities")} body={c.customer_responsibilities} />
          <Block title={dt("contract_doc.section_exclusions")} body={c.exclusions} />
          <div>
            <div className="overline mb-1">{dt("contract_doc.contract_price")}</div>
            <div className="font-mono-jc text-2xl font-bold">{money(totals.total)}</div>
            <div className="text-xs text-[var(--jc-text-soft)] mt-1">{dt("contract_doc.deposit_due_prefix")}: {money(totals.deposit)} · {dt("contract_doc.balance_prefix")}: {money(totals.balance_due)}</div>
            {allowanceTotal > 0 && (
              <div data-testid="contract-allowance-summary" className="text-xs text-[var(--jc-text-soft)] mt-2">
                <span className="font-semibold">{dt("contract_doc.includes_allowances")}:</span> {money(allowanceTotal)}
              </div>
            )}
            {tmEstimate > 0 && (
              <div data-testid="contract-tm-summary" className="text-xs text-[var(--jc-text-soft)] mt-1 italic">
                {dt("contract_doc.tm_reserve_line", money(tmEstimate))}
              </div>
            )}
          </div>
        </section>

        {(s.line_items || []).some(isTm) && (
          <section className="mt-6" data-testid="contract-tm-section">
            <div className="overline mb-2">{dt("contract_doc.tm_section_title")}</div>
            <table className="w-full text-sm border border-[var(--jc-border)]">
              <thead className="bg-[var(--jc-bg)]">
                <tr>
                  <th className="text-left px-3 py-2">{dt("contract_doc.tm_col_item")}</th>
                  <th className="text-right px-3 py-2">{dt("contract_doc.tm_col_qty")}</th>
                  <th className="text-right px-3 py-2">{dt("contract_doc.tm_col_est_total")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--jc-border)]">
                {(s.line_items || []).filter(isTm).map((l, i) => (
                  <tr key={i}>
                    <td className="px-3 py-2">
                      <div className="font-semibold">{l.name || "—"}</div>
                      <div className="text-xs text-[var(--jc-text-soft)]">{l.description}</div>
                    </td>
                    <td className="px-3 py-2 text-right font-mono-jc">{l.quantity}</td>
                    <td className="px-3 py-2 text-right font-mono-jc italic text-[var(--jc-text-soft)]">{money(l.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-xs text-[var(--jc-text-soft)] mt-2 leading-relaxed">{dt("contract_doc.tm_legend")}</p>
          </section>
        )}

        <section className="mt-8 grid md:grid-cols-2 gap-6">
          <div className="border border-[var(--jc-border)] rounded-md p-4">
            <div className="overline mb-2">{dt("contract_doc.contractor")}</div>
            <div className="font-display text-lg">{s.company?.name}</div>
            <div className="text-sm text-[var(--jc-text-soft)]">{s.company?.trade}</div>
          </div>
          <div className="border border-[var(--jc-border)] rounded-md p-4">
            <div className="overline mb-2">{dt("contract_doc.customer_signature")}</div>
            {c.status === "signed" ? (
              <>
                <div className="font-display text-2xl italic text-[var(--jc-navy)]">{c.signature_name}</div>
                <div className="text-xs text-[var(--jc-text-soft)] mt-1">{dt("contract_doc.signed_on", formatDate(c.signed_at, docLang))}</div>
              </>
            ) : (
              <div className="space-y-2">
                <input
                  value={signing}
                  onChange={(e) => setSigning(e.target.value)}
                  data-testid="contract-sign-input"
                  placeholder={t("contract_detail.sign_input_placeholder")}
                  className="w-full px-3 py-2 rounded-md border border-[var(--jc-border-strong)] focus:outline-none focus:ring-2 focus:ring-[var(--jc-blue)]"
                />
                <button onClick={sign} data-testid="contract-sign-btn" className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md jc-btn-primary"><PenLine size={14}/> {t("contract_detail.sign_contract_btn")}</button>
              </div>
            )}
          </div>
        </section>

        <footer className="mt-8 pt-6 border-t border-[var(--jc-border)] text-xs text-[var(--jc-text-soft)] leading-relaxed">
          <div className="overline mb-1">{dt("contract_doc.legal_disclaimer_heading")}</div>
          <p data-testid="contract-legal-disclaimer" className="whitespace-pre-line">{c.legal_disclaimer}</p>
        </footer>
      </div>
      <CustomerPreview open={previewOpen} onOpenChange={setPreviewOpen} kind="contract" doc={c} />
    </DashboardLayout>
  );
};

const Block = ({ title, body }) => body ? (
  <div>
    <div className="overline mb-1">{title}</div>
    <p className="leading-relaxed whitespace-pre-wrap">{body}</p>
  </div>
) : null;
